import type { PanelChain, TableBrowserPanelModel } from '.';
import type { TableBrowserColumnState } from './descriptorColumnTemplate';
import { columnStateFromTemplate, descriptorColumnTemplate } from './descriptorColumnTemplate';
import { HistoricTableModel } from './HistoricTableModel';
import type { HistoricLoaderLike } from './HistoricTableModel';
import type { TableBrowserLayoutModel } from './layoutPreferences';
import { descendantsOf, resolveAncestors } from './lineage';
import { EMPTY_PANEL_VIEW, isEmptyPanelView } from './tableBrowserUrl';
import type { TableBrowserPanelView } from './tableBrowserUrl';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { parseKey } from '@thinkalpha/table-client';
import type { FieldDescriptor, Key, RawClient, SortModel } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { DatadogGridReplayPrivacy } from '@thinkalpha/ui-table-svelte/datadogGridReplayPrivacy';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import type { Unsubscribable } from 'rxjs';
import { smartFilterToTableFilter } from 'src/components/smart-filter';
import { fieldDescriptorToFunctionDef } from 'src/features/table/utils/fieldDescriptorToFunctionDef';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import { makeTableV2QueryOptions } from 'src/models/TableV2WidgetModel/TableV2TableQuery';
import type { FullKeyType } from 'src/types';
import { v4 as uuid } from 'uuid';

export interface TableBrowserPanelModelDeps {
    sourceKey: string;
    view: TableBrowserPanelView;
    table: TableModel;
    layout: TableBrowserLayoutModel;
    /** Chain-ordered catalog keys, for ancestors and descendants. */
    catalogKeys: ReadonlySignal<readonly string[]>;
    /** The active raw proxy client, for historic tables that page through the loader. */
    proxyClient: RawClient;
    /** Test seam for historic panels; see HistoricTableModelDeps. */
    createHistoricLoader?: (client: RawClient, key: string, log: Logger) => HistoricLoaderLike;
    datadogReplayPrivacy: ReadonlySignal<DatadogGridReplayPrivacy>;
    log: Logger;
}

/** Initial backend window until the grid reports its own (matches Table v2). */
const INITIAL_BOUNDS = { firstRow: 0, windowSize: 100 };

/**
 * Query for the source table: no network. `TableModel` gets a cookie-less creation result for the
 * raw key and subscribes to it directly, which is what the legacy key tree did with a bare
 * `TableClient`. `tableCookie` is deliberately optional on the with-columns result type and every
 * `TableModel` use of it is guarded, so the amend and snapshot paths simply stay off.
 */
export function makeSourceTableQueryOptions(sourceKey: string): TableCreationQuery {
    return {
        queryKey: ['table-browser', sourceKey],
        queryFn: () => ({ key: parseKey(sourceKey), columns: [], fetchMode: undefined }) satisfies FullKeyType,
        staleTime: Infinity,
    };
}

export class TableBrowserPanelModelImpl implements TableBrowserPanelModel {
    readonly #disposableStack = new DisposableStack();
    readonly #layout: TableBrowserLayoutModel;
    readonly #view = signal<TableBrowserPanelView>(EMPTY_PANEL_VIEW);
    readonly #descriptors = signal<readonly FieldDescriptor[]>([]);
    readonly #definition = signal<unknown>(undefined);
    readonly #columnState = signal<TableBrowserColumnState | null>(null);
    readonly #collapsed = signal(false);
    readonly #filterText = signal('');
    readonly #filterError = signal<string | null>(null);
    /** Distinguishes this panel's derived table from every other view of the same source. */
    readonly #viewKey = uuid();

    readonly id = uuid();
    readonly sourceKey: string;
    readonly table: TableModel;
    readonly isHistoric: boolean;
    readonly historic: HistoricTableModel | null;
    readonly isDerived: ReadonlySignal<boolean>;
    readonly derivedKey: ReadonlySignal<string | null>;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate>;
    readonly rowCount: ReadonlySignal<number>;
    readonly error: ReadonlySignal<Error | undefined>;
    readonly datadogReplayPrivacy: ReadonlySignal<DatadogGridReplayPrivacy>;
    readonly chain: ReadonlySignal<PanelChain>;

    constructor(deps: TableBrowserPanelModelDeps) {
        this.sourceKey = deps.sourceKey;
        this.table = deps.table;
        this.#layout = deps.layout;
        this.datadogReplayPrivacy = deps.datadogReplayPrivacy;
        this.#view.value = deps.view;
        // A filter arriving from the URL is already a raw formula; show it as one.
        this.#filterText.value = deps.view.filter === null ? '' : `=${deps.view.filter}`;
        this.#columnState.value = deps.layout.readColumnState(deps.sourceKey);
        this.isHistoric = safeParseKey(deps.sourceKey)?.ex === 'H';
        this.#disposableStack.use(this.table);
        this.historic = this.isHistoric
            ? new HistoricTableModel({
                  client: deps.proxyClient,
                  key: deps.sourceKey,
                  log: deps.log,
                  createLoader: deps.createHistoricLoader,
              })
            : null;
        if (this.historic !== null) {
            this.#disposableStack.use(this.historic);
        }

        // Historic sort is local, so it never moves the panel onto a derived table.
        this.isDerived = computed(() => this.historic === null && !isEmptyPanelView(this.#view.value));
        this.derivedKey = computed(() => {
            const key = this.table.keySignal.value;
            return this.isDerived.value && key !== '' && key !== this.sourceKey ? key : null;
        });
        this.columnTemplate = computed(() =>
            descriptorColumnTemplate(
                this.sourceKey,
                this.#descriptors.value,
                this.#columnState.value,
                this.#view.value.sort,
            ),
        );
        this.rowCount = this.historic === null ? this.table.countSignal : this.historic.rowCount;
        this.error = this.table.errorSignal;
        this.chain = computed(() => ({
            ancestors: resolveAncestors(deps.catalogKeys.value, this.sourceKey),
            descendants: descendantsOf(deps.catalogKeys.value, this.sourceKey),
        }));

        const parsedKey = safeParseKey(deps.sourceKey);
        if (parsedKey === null) {
            deps.log.warn({ message: 'Table Browser panel key has no class suffix', key: deps.sourceKey });
        }
        if (this.historic !== null) {
            const historic = this.historic;
            this.#disposableStack.defer(
                effect(() => {
                    this.#descriptors.value = historic.descriptors.value;
                }),
            );
            historic.setSort(deps.view.sort);
            historic.loadMore();
        }
        if (!this.isHistoric && parsedKey !== null) {
            const queryOptions = computed<TableCreationQuery>(() => {
                const view = this.#view.value;
                if (isEmptyPanelView(view)) {
                    return makeSourceTableQueryOptions(this.sourceKey);
                }
                return makeTableV2QueryOptions(parsedKey, view.sort, view.filter ?? undefined, this.#viewKey);
            });
            this.table.init({
                queryOptions,
                initBounds: INITIAL_BOUNDS,
                devtoolsName: 'Admin/tableBrowser',
            });
            // Descriptors and the definition come from the live TableClient, which TableModel
            // recreates per generation; the key signal changes with it.
            this.#disposableStack.defer(
                effect(() => {
                    void this.table.keySignal.value;
                    const client = this.table.tableClient;
                    if (client === null) {
                        return;
                    }
                    const subscriptions: Unsubscribable[] = [
                        client.descriptor$.subscribe((descriptors) => {
                            this.#descriptors.value = descriptors;
                        }),
                        client.definition$.subscribe((definition) => {
                            this.#definition.value = definition;
                        }),
                    ];
                    return () => {
                        for (const subscription of subscriptions) {
                            subscription.unsubscribe();
                        }
                    };
                }),
            );
        }
    }

    get view(): ReadonlySignal<TableBrowserPanelView> {
        return this.#view;
    }

    get descriptors(): ReadonlySignal<readonly FieldDescriptor[]> {
        return this.#descriptors;
    }

    get definition(): ReadonlySignal<unknown> {
        return this.#definition;
    }

    get columnState(): ReadonlySignal<TableBrowserColumnState | null> {
        return this.#columnState;
    }

    get collapsed(): ReadonlySignal<boolean> {
        return this.#collapsed;
    }

    get filterText(): ReadonlySignal<string> {
        return this.#filterText;
    }

    get filterError(): ReadonlySignal<string | null> {
        return this.#filterError;
    }

    clearSort(): void {
        this.setSort(null);
    }

    applyFilterText(text: string): void {
        this.#filterText.value = text;
        const trimmed = text.trim();
        if (trimmed === '') {
            this.#filterError.value = null;
            this.setFilter(null);
            return;
        }
        const functionDefs = this.#descriptors.value.map(fieldDescriptorToFunctionDef);
        const compiled = smartFilterToTableFilter(trimmed, functionDefs);
        if (compiled === undefined || compiled === '') {
            this.#filterError.value = trimmed.startsWith('=')
                ? 'The formula did not compile to a boolean condition against the fields of this table.'
                : 'This table has no text fields to search; use an =formula instead.';
            return;
        }
        this.#filterError.value = null;
        this.setFilter(compiled);
    }

    setSort(sort: SortModel | null): void {
        const next = sort === null || Object.keys(sort).length === 0 ? null : sort;
        // Key order is sort precedence, so compare entries in order; a deep-equal ignores it.
        if (sortModelsEqual(this.#view.value.sort, next)) {
            return;
        }
        this.#view.value = { ...this.#view.value, sort: next };
        this.historic?.setSort(next);
    }

    setFilter(filter: string | null): void {
        const next = filter === null || filter.trim() === '' ? null : filter;
        if (this.#view.value.filter === next) {
            return;
        }
        this.#view.value = { ...this.#view.value, filter: next };
    }

    applyView(view: TableBrowserPanelView): void {
        this.setSort(view.sort);
        this.setFilter(view.filter);
        this.#filterText.value = view.filter === null ? '' : `=${view.filter}`;
        this.#filterError.value = null;
    }

    resetView(): void {
        this.#filterText.value = '';
        this.#filterError.value = null;
        if (isEmptyPanelView(this.#view.value)) {
            return;
        }
        this.#view.value = EMPTY_PANEL_VIEW;
    }

    updateColumnTemplate(template: ColumnTemplate): void {
        const next = columnStateFromTemplate(template);
        if (isEqual(this.#columnState.value, next)) {
            return;
        }
        this.#columnState.value = next;
        this.#layout.writeColumnState(this.sourceKey, next);
    }

    setCollapsed(collapsed: boolean): void {
        this.#collapsed.value = collapsed;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

function sortModelsEqual(a: SortModel | null, b: SortModel | null): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    const left = Object.entries(a);
    const right = Object.entries(b);
    return (
        left.length === right.length &&
        left.every(([field, direction], index) => right[index][0] === field && right[index][1] === direction)
    );
}

function safeParseKey(key: string): Key | null {
    try {
        return parseKey(key);
    } catch {
        return null;
    }
}
